import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  PurchaseOrder,
  CreatePurchaseOrderInput,
  DispatchPurchaseOrderInput,
  RevisePricePurchaseOrderInput,
  ApprovePriceRevisionInput,
  ReceivePurchaseOrderInput,
  ResolveDiscrepancyInput,
  CancelPurchaseOrderInput,
} from '@/domain/entities/purchase-order.entity'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { PurchaseOrderDto } from './purchase-order.dto'
import { purchaseOrderMapper } from './purchase-order.mapper'

export interface PurchaseOrderQueryParams {
  outlet_id?: string
  source_outlet_id?: string
  source_type?: string
  status?: string
  from?: string
  to?: string
  search?: string
  page?: number
  limit?: number
}

export class PurchaseOrderHttpRepository {
  async list(params: PurchaseOrderQueryParams = {}): Promise<Paged<PurchaseOrder>> {
    const raw = await httpClient.get<unknown>('/purchase-orders', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        outlet_id: params.outlet_id,
        source_outlet_id: params.source_outlet_id,
        source_type: params.source_type,
        status: params.status,
        from: params.from,
        to: params.to,
        search: params.search,
      },
    })
    const paged = unwrapPaged<PurchaseOrderDto>(raw, params.page ?? 1, params.limit ?? 20)
    return {
      ...paged,
      items: paged.items.map(purchaseOrderMapper.toEntity),
    }
  }

  async getById(id: string): Promise<PurchaseOrder> {
    const raw = await httpClient.get<unknown>(`/purchase-orders/${id}`)
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async create(input: CreatePurchaseOrderInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>('/purchase-orders', input)
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async dispatch(input: DispatchPurchaseOrderInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>(`/purchase-orders/${input.po_id}/dispatch`, {
      items: input.items,
      note: input.note,
    })
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async revisePrice(input: RevisePricePurchaseOrderInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>(`/purchase-orders/${input.po_id}/revise-price`, {
      items: input.items,
      note: input.note,
    })
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async approvePriceRevision(input: ApprovePriceRevisionInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>(
      `/purchase-orders/${input.po_id}/approve-price-revision`,
      {
        approved: input.approved,
        reason: input.reason,
      },
    )
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async receive(input: ReceivePurchaseOrderInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>(`/purchase-orders/${input.po_id}/receive`, {
      items: input.items,
      note: input.note,
    })
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async resolveDiscrepancy(input: ResolveDiscrepancyInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>(
      `/purchase-orders/${input.po_id}/resolve-discrepancy`,
      {
        approved: input.approved,
        note: input.note,
      },
    )
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }

  async cancel(input: CancelPurchaseOrderInput): Promise<PurchaseOrder> {
    const raw = await httpClient.post<unknown>(`/purchase-orders/${input.po_id}/cancel`, {
      reason: input.reason,
    })
    return purchaseOrderMapper.toEntity(unwrap<PurchaseOrderDto>(raw))
  }
}
